import {
  ProximityPolicyRuleSubcategorySpecType,
  ProximityPolicyRuleType,
} from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import ButtonSwitchMulti from '../components/actions/ButtonSwitchMulti';
import { PolicyFilter } from '../components/policies/PolicyFilter';
import PolicyFilterItem from '../components/policies/PolicyFilterItem';
import RuleContainer, {
  RuleContainerProps,
} from '../components/policies/RuleContainer';
import ProximityPolicyGroup from './ProximityPolicyCategory';
import {
  getProximityConstraintTypeOptions,
  getProximitySubcategoryOptions,
} from './proximityPolicy.helper';
import {
  proximityPolicyRule,
  proximityPolicySelectedIdentity,
} from './proximityPolicy.state';

export type ProximityPolicyRuleProps = {
  ruleId: string;
  index?: number;
  className?: string;
  isDefault?: boolean;
  isRemovable?: boolean;
  isNew?: boolean;
  hasIssue?: boolean;
  isDisabled?: boolean;
  isCheckLoading: boolean;
  productCount?: [number, number];
  locationCount?: [total: number, exclusive: number, empty?: number];

  disableProductSelection?: boolean;
  canDelete?: boolean;
  onDeleteClick?: () => void;
  canRename?: boolean;
};

const ProximityPolicyRule: React.FC<ProximityPolicyRuleProps> = props => {
  const { t } = useTranslation('simulation');
  const { ruleId } = props;
  const [rule, updateRule] = useRecoilState(proximityPolicyRule(ruleId));
  const [selectedIdentity, setSelectedIdentity] = useRecoilState(
    proximityPolicySelectedIdentity,
  );
  // const checkResult = useRecoilValue(proximityPolicyCheckResult);
  // const ruleCheckResult = _.find(
  //   checkResult?.result?.rules,
  //   r => r.id === ruleId,
  // );

  function renameRule(newTitle: string) {
    updateRule(
      ensureBreakByDefault({
        ...rule,
        title: newTitle,
      }),
    );
  }

  // Ensure breakBy always has at least one default category
  const ensureBreakByDefault = rule => {
    if (_.isEmpty(rule.subcategories?.breakBy)) {
      return {
        ...rule,
        subcategories: {
          ...rule.subcategories,
          breakBy: [
            {
              type: ProximityPolicyRuleSubcategorySpecType.SKU_GROUP,
            },
          ],
        },
      };
    }
    return rule;
  };

  const hasIssue = false; //ruleCheckResult?.??? === false;
  const isActive = selectedIdentity?.ruleId === ruleId;
  const containerParams: RuleContainerProps = {
    id: rule.id,
    title: rule.title,
    isCollapsible: true,
    isRemovable: props.canDelete,
    isActive,
    isNew: props.isNew,
    orderCounter: props.index,
    onDeleteClick: () => {
      if (props.onDeleteClick) {
        props.onDeleteClick();
      }
    },
    canRename: props.canRename,
    onTitleChange: title => renameRule(title),
  };
  const isSelectedRule = selectedIdentity?.ruleId === ruleId;

  const isActiveConstraintsFilter =
    isSelectedRule && selectedIdentity?.type === 'constraints';

  const isActiveSubcategoriesFilter =
    isSelectedRule && selectedIdentity?.type === 'subcategories';

  const constraint = _.head(rule?.constraint);
  const subcategories = rule?.subcategories;

  const ruleTypeOptions: { key: ProximityPolicyRuleType; label: string }[] = [
    {
      key: ProximityPolicyRuleType.BETWEEN_SUBCATEGORIES,
      label: t`Sub-Categories`,
    },
    {
      key: ProximityPolicyRuleType.BETWEEN_CATEGORIES,
      label: t`Categories`,
    },
  ];

  // Get translated options
  const subcategoryOptions = getProximitySubcategoryOptions(t);
  const constraintTypeOptions = getProximityConstraintTypeOptions(t);

  // Create lookup maps for translations
  const subcategoryMap = _.keyBy(subcategoryOptions, 'id');
  const constraintTypeMap = _.keyBy(constraintTypeOptions, 'id');

  return (
    <RuleContainer
      {...containerParams}
      isDisabled={props.isDisabled}
      hasIssue={hasIssue}
      hasIcon
      hasColorMode={false}
      hasOrder
    >
      {/* {hasIssue && <ProximityPolicyIssue details={ruleCheckResult?.details} />} */}

      <ProximityPolicyGroup
        title={t`Filter By`}
        ruleId={ruleId}
        category="main"
      />

      <div className="flex flex-col gap-2 p-2">
        <ButtonSwitchMulti
          title={t`Between`}
          buttonType="switchon"
          autoSize
          options={ruleTypeOptions}
          selectedIndex={
            rule.type === ProximityPolicyRuleType.BETWEEN_SUBCATEGORIES ? 0 : 1
          }
          onClick={index =>
            updateRule(
              ensureBreakByDefault({
                ...rule,
                type: ruleTypeOptions[index].key,
              }),
            )
          }
        />
      </div>

      <div className="flex flex-col gap-2 p-1">
        {rule.type === ProximityPolicyRuleType.BETWEEN_CATEGORIES && (
          <ProximityPolicyGroup
            title={t`Categories`}
            ruleId={ruleId}
            category="secondary"
          />
        )}

        {rule.type === ProximityPolicyRuleType.BETWEEN_SUBCATEGORIES && (
          <PolicyFilter
            label={t`Sub Categories:`}
            isActive={isActiveSubcategoriesFilter}
            isDisabled={props.isDisabled}
            onClick={() =>
              setSelectedIdentity(
                isActiveSubcategoriesFilter
                  ? null
                  : {
                      ruleId,
                      type: 'subcategories',
                    },
              )
            }
          >
            {!_.isEmpty(subcategories?.groupBy) && (
              <PolicyFilterItem
                name={t`Group by`}
                isActive={isActiveSubcategoriesFilter}
                value={_(subcategories?.groupBy)
                  .map(g => subcategoryMap[g.type]?.title || g.type)
                  .join(', ')}
              />
            )}

            {!_.isEmpty(subcategories?.breakBy) && (
              <PolicyFilterItem
                name={t`Split by`}
                isActive={isActiveSubcategoriesFilter}
                value={_(subcategories?.breakBy)
                  .map(g => subcategoryMap[g.type]?.title || g.type)
                  .join(', ')}
              />
            )}
          </PolicyFilter>
        )}

        <PolicyFilter
          label={t`Proximity Constraints:`}
          isActive={isActiveConstraintsFilter}
          isDisabled={props.isDisabled}
          onClick={() =>
            setSelectedIdentity(
              isActiveConstraintsFilter
                ? null
                : {
                    ruleId,
                    type: 'constraints',
                  },
            )
          }
        >
          <PolicyFilterItem
            name={t`Type`}
            isActive={isActiveConstraintsFilter}
            value={
              constraint?.constraintType
                ? constraintTypeMap[constraint.constraintType]?.title
                : ''
            }
          />
          <PolicyFilterItem
            name={t`Distance`}
            isActive={isActiveConstraintsFilter}
            value={constraint?.distance?.distance?.toString()}
          />
          {constraint?.distance?.includeHeightFromFloor && (
            <PolicyFilterItem
              name={t`Include HFF`}
              isActive={isActiveConstraintsFilter}
              value={
                constraint?.distance?.includeHeightFromFloor ? t`Yes` : t`No`
              }
            />
          )}
        </PolicyFilter>
      </div>
    </RuleContainer>
  );
};
export default ProximityPolicyRule;
